import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Table, Button, Input, Select, Tag, Space, Tooltip, Popconfirm, Empty, message } from "antd";
import {
  SearchOutlined, PhoneOutlined, EyeOutlined, DeleteOutlined,
  EditOutlined, PlusOutlined, UploadOutlined, ReloadOutlined, DownloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import employeeService from "../../services/employeeService";
import exportService   from "../../services/exportService";
import JourneyTimeline from "../../components/JourneyTimeline";
import StatusBadge from "../../components/StatusBadge";
import { useAllSchedules } from "../../context/MasterDataContext";
import "./employeelist.css";

const { Option } = Select;

const C = {
  blue:"#1E6FBF", orange:"#E8871A", green:"#16a34a",
  red:"#dc2626", purple:"#6366f1",
  text1:"#1a2d4a", text2:"#4a6580", text3:"#7a96b0",
  surf2:"#f5f8fc", border:"#dce6f0",
};

// ── helpers ────────────────────────────────────────────────────
// ── Journey complete — employee joined 180+ days ago ──
const isLegacyEmployee = (emp) => {
  if (!emp?.dateOfJoining) return false;
  return dayjs().diff(dayjs(emp.dateOfJoining), 'day') >= 180;
};

// Employee is "currently No Response" only if their latest No Response stage
// has NOT been superseded by a Completed call on a later stage (higher day).
// Example: No Response on day-60 (June) but Completed on day-90 (July) → NOT No Response.
const dayFromKey = key => parseInt((key || '').replace(/\D/g, ''), 10) || 0;
function isCurrentlyNoResponse(emp) {
  const calls = (emp.calls || []).filter(c => c.status === 'No Response');
  if (calls.length === 0) return false;
  const maxNoRespDay = Math.max(...calls.map(c => dayFromKey(c.callKey)));
  return !(emp.calls || []).some(
    c => c.status === 'Completed' && dayFromKey(c.callKey) > maxNoRespDay
  );
}

// Returns one of: Completed | NA | No Response | Overdue | Today | Next7Days | Upcoming
function getCallStatus(employee, callKey, stageDays) {
  if (isLegacyEmployee(employee)) return "Completed";
  const calls = employee.calls || [];
  const call  = Array.isArray(calls) ? calls.find(c => c.callKey === callKey) : null;
  if (call) return call.status || "Upcoming";

  // No call logged — classify by how overdue/upcoming the due date is
  if (!employee.dateOfJoining || stageDays == null) return "Upcoming";
  const todayStr  = dayjs().format("YYYY-MM-DD");
  const in7Str    = dayjs().add(6, "day").format("YYYY-MM-DD");
  const grace7Str = dayjs().subtract(7, "day").format("YYYY-MM-DD"); // 7-day grace window
  const dueStr    = dayjs(employee.dateOfJoining).add(stageDays, "day").format("YYYY-MM-DD");
  if (dueStr <  grace7Str)  return "Overdue";   // 8+ days past due → critical
  if (dueStr <  todayStr)   return "Pending";   // 1–7 days past due → grace window
  if (dueStr === todayStr)  return "Today";
  if (dueStr <= in7Str)     return "Next7Days";
  return "Upcoming";
}

function getJourneyProgress(employee, stages) {
  if (isLegacyEmployee(employee)) return 100;
  if (!stages?.length) return 0;
  const completed = stages.filter(({ key, day }) => getCallStatus(employee, key, day) === "Completed").length;
  return Math.round((completed / stages.length) * 100);
}

// Resolve the correct stages for an employee based on their cohortName
function resolveScheduleForEmp(employee, allSchedules) {
  const name = (employee?.cohortName || "").trim().toLowerCase();
  const found = allSchedules.find(s =>
    name === s.scheduleKey || name.startsWith(s.scheduleKey)
  );
  return found?.stages ?? allSchedules[0]?.stages ?? [];
}

function daysSinceJoining(dateOfJoining) {
  if (!dateOfJoining) return 0;
  return dayjs().diff(dayjs(dateOfJoining), "day");
}

export default function EmployeeList() {
  const navigate      = useNavigate();
  const [searchParams] = useSearchParams();
  const allSchedules  = useAllSchedules();   // ← all cohort schedules

  const [employees,    setEmployees]    = useState([]);
  const [attrStats,    setAttrStats]    = useState(null);
  const [loading,      setLoading]      = useState(true);
  // Read initial tab/status from URL params (e.g. ?tab=active&status=pending)
  const [activeTab,    setActiveTab]    = useState(searchParams.get("tab") || "active");
  const [search,       setSearch]       = useState("");
  const [filterDiv,    setFilterDiv]    = useState("all");
  const [filterStatus, setFilterStatus] = useState(searchParams.get("status") || "all");
  const [filterDesig,  setFilterDesig]  = useState("all");
  const [selectedRows, setSelectedRows] = useState([]);

  // ── client-side pagination only ────────────────────────────
  const [page,  setPage]  = useState(1);
  const [limit, setLimit] = useState(20);
  const [exporting, setExporting] = useState(false);

  // ── Export to Excel ────────────────────────────────────────
  async function handleExport() {
    setExporting(true);
    try {
      await exportService.downloadEmployeeReport();
      message.success("Report downloaded successfully");
    } catch (err) {
      message.error(err.message || "Failed to generate report");
    } finally {
      setExporting(false);
    }
  }

  // ── load ALL employees once — all filtering is client-side ──
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [res, statsRes] = await Promise.all([
        employeeService.getAll({ page: 1, limit: 9999 }),
        employeeService.getAttritionStats().catch(() => null),
      ]);
      const rows = res.data?.data || res.data || [];
      setEmployees(Array.isArray(rows) ? rows : []);
      if (statsRes?.data) setAttrStats(statsRes.data);
    } catch (err) {
      message.error(err.message || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── delete ─────────────────────────────────────────────────
  async function handleDelete(id) {
    try {
      await employeeService.remove(id);
      message.success("Employee removed successfully");
      loadData();
    } catch (err) {
      message.error(err.message || "Failed to remove employee");
    }
  }

  async function handleDeleteSelected() {
    try {
      await Promise.all(selectedRows.map(id => employeeService.remove(id)));
      message.success(`${selectedRows.length} employees removed`);
      setSelectedRows([]);
      loadData();
    } catch (err) {
      message.error(err.message || "Failed to remove selected employees");
    }
  }

  // ── derived lists for filter dropdowns ────────────────────
  const divisions    = useMemo(() => [...new Set(employees.map(e => e.division).filter(Boolean))],    [employees]);
  const designations = useMemo(() => [...new Set(employees.map(e => e.designation).filter(Boolean))], [employees]);

  // ── Tab split ─────────────────────────────────────────────────
  const DEC_2025_MS = dayjs('2025-12-01').valueOf();

  // Exclusion rules: CCD/Venus (join>Dec'25 + resign>=May'26), After-180 days, Y-code
  const CLOSED_DIVS_EL = new Set(['ccd', 'venus']);
  const MAY_2026_MS_EL = dayjs('2026-05-01').valueOf();
  const isExcludedEl = e => {
    const doj = e.dateOfJoining || e.cohortDate;
    const div = (e.division || '').trim().toLowerCase();
    if (CLOSED_DIVS_EL.has(div) && doj && dayjs(doj).valueOf() > DEC_2025_MS &&
        e.resignedDate && dayjs(e.resignedDate).valueOf() >= MAY_2026_MS_EL) return true;
    if (doj && e.resignedDate && dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180) return true;
    if ((e.empCode || '').toUpperCase().startsWith('Y')) return true;
    return false;
  };
  // Is this employee a pre-Dec'25 joiner (Oct/Nov 2025)?
  const isPreDec25 = e => {
    const doj = e.dateOfJoining || e.cohortDate;
    return doj && dayjs(doj).valueOf() < DEC_2025_MS;
  };

  const activeEmployees   = useMemo(() => employees.filter(e => !e.isResigned), [employees]);
  const resignedEmployees = useMemo(() => employees.filter(e =>  e.isResigned), [employees]);

  // Active tab: Dec'25+ employees who have NOT yet completed 180 days
  const inJourneyEmployees = useMemo(
    () => activeEmployees.filter(e => !isLegacyEmployee(e) && !isPreDec25(e)),
    [activeEmployees]
  );
  // Journey Completed tab: Dec'25+ employees who completed 180 days
  //   • Active employees with tenure ≥ 180 days today
  //   • Resigned employees whose tenure AT resignation was ≥ 180 days
  const journeyCompletedEmployees = useMemo(() => {
    const activeCompleted = activeEmployees.filter(e => isLegacyEmployee(e) && !isPreDec25(e));
    const resignedAfter180 = resignedEmployees.filter(e => {
      if (isPreDec25(e)) return false;
      const doj = e.dateOfJoining || e.cohortDate;
      if (!doj || !e.resignedDate) return false;
      return dayjs(e.resignedDate).diff(dayjs(doj), 'day') >= 180;
    });
    return [...activeCompleted, ...resignedAfter180];
  }, [activeEmployees, resignedEmployees]);
  // Resigned tab: Dec'25+ attrition resigned only (excluded employees go toward the active KPI count)
  const filteredResignedEmployees = useMemo(
    () => resignedEmployees.filter(e => !isPreDec25(e) && !isExcludedEl(e)),
    [resignedEmployees]
  );
  // Total Dec'25+ enrolled (for KPI-consistent active count: total - resigned - completed = 612)
  const decPlusTotalEl = useMemo(
    () => employees.filter(e => !isPreDec25(e)).length,
    [employees]
  );
  const inJourneyKpiCount = decPlusTotalEl - filteredResignedEmployees.length - journeyCompletedEmployees.length;

  // Pending Calls tab: active + journey-completed employees who have ≥1 unresolved call
  const pendingCallsEmployees = useMemo(() => {
    // Active employees: use getCallStatus for Overdue/Pending/Missed
    const activePending = inJourneyEmployees.filter(emp => {
      const stages = resolveScheduleForEmp(emp, allSchedules);
      return stages.some(({ key, day }) => {
        const s = getCallStatus(emp, key, day);
        return s === 'Overdue' || s === 'Pending' || s === 'Missed';
      });
    });
    // Journey-completed: only flag if any call has explicit 'Missed' status
    // Unlogged calls = journey window passed, treated as closed (not pending)
    const completedPending = journeyCompletedEmployees.filter(emp => {
      const calls = emp.calls || [];
      return calls.some(c => c.status === 'Missed');
    });
    return [...activePending, ...completedPending];
  }, [inJourneyEmployees, journeyCompletedEmployees, allSchedules]);

  const tabEmployees =
    activeTab === "resigned"      ? filteredResignedEmployees :
    activeTab === "completed"     ? journeyCompletedEmployees :
    activeTab === "pending_calls" ? pendingCallsEmployees :
    inJourneyEmployees;

  // ── All filtering is client-side — search, division, designation, status ──
  const filtered = useMemo(() => {
    return tabEmployees.filter(emp => {
      // Search: name, code, hq, manager
      if (search) {
        const s = search.toLowerCase();
        const match =
          (emp.empName    || "").toLowerCase().includes(s) ||
          (emp.empCode    || "").toLowerCase().includes(s) ||
          (emp.hq         || "").toLowerCase().includes(s) ||
          (emp.managerName|| "").toLowerCase().includes(s) ||
          (emp.designation|| "").toLowerCase().includes(s);
        if (!match) return false;
      }
      // Division filter
      if (filterDiv !== "all" && emp.division !== filterDiv) return false;
      // Designation filter
      if (filterDesig !== "all" && emp.designation !== filterDesig) return false;
      // Status filter (call journey status)
      if (filterStatus !== "all") {
        const empStages = resolveScheduleForEmp(emp, allSchedules);
        let matchStatus = false;
        if (isLegacyEmployee(emp)) {
          matchStatus = filterStatus === "completed";
        } else if (filterStatus === "today")
          matchStatus = empStages.some(({ key, day }) => getCallStatus(emp, key, day) === "Today");
        else if (filterStatus === "next7days")
          matchStatus = empStages.some(({ key, day }) => getCallStatus(emp, key, day) === "Next7Days");
        else if (filterStatus === "pending")
          matchStatus = empStages.some(({ key, day }) => {
            const s = getCallStatus(emp, key, day);
            return s === "Overdue" || s === "Pending" || s === "Missed";
          });
        else if (filterStatus === "missed")
          matchStatus = empStages.some(({ key, day }) => getCallStatus(emp, key, day) === "Missed");
        else if (filterStatus === "noresponse")
          matchStatus = isCurrentlyNoResponse(emp);
        else if (filterStatus === "completed")
          matchStatus = empStages.every(({ key, day }) => getCallStatus(emp, key, day) === "Completed");
        else if (filterStatus === "upcoming")
          matchStatus = empStages.every(({ key, day }) => getCallStatus(emp, key, day) === "Upcoming");
        if (!matchStatus) return false;
      }
      return true;
    });
  }, [tabEmployees, search, filterDiv, filterDesig, filterStatus, allSchedules]);

  // ── Designation-wise counts (from loaded employees) ────────────
  const designationCounts = useMemo(() => {
    const map = {};
    tabEmployees.forEach(e => {
      const d = e.designation || 'Unknown';
      map[d] = (map[d] || 0) + 1;
    });
    return Object.entries(map)
      .map(([designation, count]) => ({ designation, count }))
      .sort((a, b) => b.count - a.count);
  }, [tabEmployees]);

  // ── KPI counts — computed from ALL loaded employees ──────────
  const computedKpi = useMemo(() => {
    let pending = 0, missed = 0, upcoming = 0, todayCount = 0, week7Count = 0, noResponse = 0;
    // Only compute call KPIs for active, non-legacy employees
    activeEmployees.forEach(emp => {
      if (isLegacyEmployee(emp)) return; // Legacy = journey closed, not pending
      const stages = resolveScheduleForEmp(emp, allSchedules);
      const isNA = ({ key, day }) => getCallStatus(emp, key, day) === 'NA';
      if (stages.some(({ key, day }) => {
        const s = getCallStatus(emp, key, day);
        return !isNA({key,day}) && (s === 'Overdue' || s === 'Pending' || s === 'Missed');
      })) pending++;
      if (stages.some(({ key, day }) => getCallStatus(emp, key, day) === 'Today'))     todayCount++;
      if (stages.some(({ key, day }) => getCallStatus(emp, key, day) === 'Next7Days')) week7Count++;
      if (stages.some(({ key, day }) => getCallStatus(emp, key, day) === 'Missed'))    missed++;
      if (stages.every(({ key, day }) => ["Upcoming","Next7Days"].includes(getCallStatus(emp, key, day)))) upcoming++;
      if (isCurrentlyNoResponse(emp)) noResponse++;
    });
    return {
      total:        activeTab === "resigned"      ? filteredResignedEmployees.length :
                    activeTab === "completed"     ? journeyCompletedEmployees.length :
                    activeTab === "pending_calls" ? pendingCallsEmployees.length :
                    inJourneyEmployees.length,
      activeCount:  inJourneyEmployees.length + journeyCompletedEmployees.length,
      resignedCount:filteredResignedEmployees.length,
      pending, missed, noResponse, new: upcoming, todayCount, week7Count,
    };
  }, [activeEmployees, filteredResignedEmployees, inJourneyEmployees, journeyCompletedEmployees, pendingCallsEmployees, activeTab, allSchedules]);

  // ── columns — identical to your original ──────────────────
  const columns = [
    {
      title: "#", width: 50,
      render: (_, __, i) => <span style={{ color: C.text3, fontSize: 12 }}>{((page - 1) * limit) + i + 1}</span>,
    },
    {
      title: "Employee", dataIndex: "empName", width: 200,
      sorter: (a, b) => a.empName.localeCompare(b.empName),
      render: (v, rec) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
            background: "linear-gradient(135deg,#1E6FBF,#1B2D6B)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700, color: "white",
          }}>
            {v?.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div
              style={{ fontWeight: 600, color: C.blue, fontSize: 13, cursor: "pointer" }}
              onClick={() => navigate(`/employees/${rec.id}`)}
            >
              {v}
            </div>
            <div style={{ fontSize: 11, color: C.text3 }}>{rec.empCode} · {rec.designation}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Division / HQ", width: 140,
      sorter: (a, b) => (a.division || "").localeCompare(b.division || ""),
      render: (_, rec) => (
        <div>
          <Tag style={{ background: "#eaf1f8", border: "1px solid #c5d6e8", color: C.text2, borderRadius: 6, fontSize: 11, marginBottom: 3 }}>
            {rec.division || "—"}
          </Tag>
          <div style={{ fontSize: 11, color: C.text3 }}>{rec.hq}</div>
        </div>
      ),
    },
    {
      title: "DOJ / Day", width: 120,
      sorter: (a, b) => dayjs(a.dateOfJoining).unix() - dayjs(b.dateOfJoining).unix(),
      render: (_, rec) => {
        const days = daysSinceJoining(rec.dateOfJoining);
        const pct  = Math.min((days / 180) * 100, 100);
        return (
          <div>
            <div style={{ color: C.text1, fontSize: 12 }}>{dayjs(rec.dateOfJoining).format("DD MMM YY")}</div>
            <div style={{ fontSize: 11, color: C.orange, fontWeight: 600, marginBottom: 4 }}>Day {days} / 180</div>
            <div style={{ height: 3, background: "#eaf1f8", borderRadius: 2, overflow: "hidden" }}>
              <div style={{
                height: "100%", width: `${pct}%`, borderRadius: 2,
                background: days > 180 ? C.red : days > 150 ? C.orange : C.blue,
                transition: "width 0.4s ease",
              }} />
            </div>
          </div>
        );
      },
    },
    {
      title: "Manager", dataIndex: "rmName", width: 150,
      render: (v, rec) => (
        <div>
          <div style={{ color: C.text2, fontSize: 12 }}>{v || "—"}</div>
          <div style={{ fontSize: 11, color: C.text3 }}>{rec.rmCode || ""}</div>
        </div>
      ),
    },
    {
      title: "Day-10", width: 100,
      render: (_, rec) => <StatusBadge status={getCallStatus(rec, "day10", 10)} size="small" />,
    },
    {
      title: "Day-30", width: 100,
      render: (_, rec) => <StatusBadge status={getCallStatus(rec, "day30", 30)} size="small" />,
    },
    {
      title: "Next Due", width: 140,
      render: (_, rec) => {
        const recStages = resolveScheduleForEmp(rec, allSchedules);
        // Find the first non-completed stage
        const nextDue = recStages.find(({ key, day }) => {
          const s = getCallStatus(rec, key, day);
          return !["Completed", "NA"].includes(s);
        });

        if (!nextDue)
          return <span style={{ color: C.green, fontSize: 11, fontWeight: 700 }}>✅ All done</span>;

        const status = getCallStatus(rec, nextDue.key, nextDue.day);
        const dueStr = rec.dateOfJoining
          ? dayjs(rec.dateOfJoining).add(nextDue.day, "day").format("DD MMM")
          : "";

        const badge = (icon, label, bg, color, border) => (
          <div style={{ display:"inline-flex", flexDirection:"column", alignItems:"flex-start", gap:1 }}>
            <span style={{
              display:"inline-flex", alignItems:"center", gap:4,
              background:bg, color, border:`1.5px solid ${border}`,
              borderRadius:8, padding:"3px 8px", fontSize:11, fontWeight:700,
            }}>{icon} {nextDue.label}</span>
            {dueStr && <span style={{ fontSize:10, color:C.text3, paddingLeft:2 }}>Due {dueStr}</span>}
          </div>
        );

        if (status === "Overdue")   return badge("🔴", nextDue.label, "#fff0f0", "#b91c1c", "#fca5a5");
        if (status === "Today")     return badge("🔴", nextDue.label, "#fef2f2", "#dc2626", "#dc2626");
        if (status === "Next7Days") return badge("🟡", nextDue.label, "#fffbeb", "#b45309", "#fcd34d");
        if (status === "Missed")    return badge("⚠️", nextDue.label, "#fff0f0", "#b91c1c", "#fca5a5");
        if (status === "Pending")   return badge("📞", nextDue.label, "#fff7ed", "#c2410c", "#fb923c");
        return badge("⏳", nextDue.label, "#f5f3ff", "#6366f1", "#c4b5fd");
      },
    },
    {
      title: "Journey", width: 160,
      render: (_, rec) => (
        <div>
          <JourneyTimeline employee={rec} mini />
          <div style={{ fontSize: 10, color: C.text3, marginTop: 3 }}>
            {getJourneyProgress(rec, resolveScheduleForEmp(rec, allSchedules))}% complete
          </div>
        </div>
      ),
    },
    {
      title: "Actions", width: 155, fixed: "right",
      render: (_, rec) => (
        <Space size={6}>
          <Tooltip title="View Detail">
            <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/employees/${rec.id}`)} style={{ borderRadius: 7 }} />
          </Tooltip>
          <Tooltip title="Edit Profile">
            <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/employees/${rec.id}?tab=profile`)} style={{ borderRadius: 7 }} />
          </Tooltip>
          <Tooltip title="Log Call">
            <Button size="small" icon={<PhoneOutlined />} type="primary" onClick={() => navigate(`/employees/${rec.id}`)} style={{ borderRadius: 7 }} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this employee?"
              description="This cannot be undone."
              onConfirm={() => handleDelete(rec.id)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" icon={<DeleteOutlined />} danger style={{ borderRadius: 7 }} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowClassName = rec => {
    if (isLegacyEmployee(rec)) return "";
    const stages = resolveScheduleForEmp(rec, allSchedules);
    if (stages.some(({ key, day }) => getCallStatus(rec, key, day) === "Today"))   return "row-due-today";
    if (stages.some(({ key, day }) => getCallStatus(rec, key, day) === "Overdue")) return "row-at-risk";
    if (stages.some(({ key, day }) => getCallStatus(rec, key, day) === "Pending")) return "row-due-week";
    if (stages.some(({ key, day }) => getCallStatus(rec, key, day) === "Next7Days")) return "row-due-week";
    return "";
  };

  // ── Resigned employees table columns ──────────────────────
  const resignedColumns = [
    {
      title: "#", width: 50,
      render: (_, __, i) => <span style={{ color: C.text3, fontSize: 12 }}>{((page-1)*limit)+i+1}</span>,
    },
    {
      title: "Employee", dataIndex: "empName", width: 220,
      sorter: (a, b) => a.empName.localeCompare(b.empName),
      render: (v, rec) => (
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{
            width:36, height:36, borderRadius:"50%", flexShrink:0,
            background:"linear-gradient(135deg,#dc2626,#7f1d1d)",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:13, fontWeight:700, color:"white",
          }}>
            {v?.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight:600, color:"#7f1d1d", fontSize:13, cursor:"pointer" }}
              onClick={() => navigate(`/employees/${rec.id}`)}>
              {v}
            </div>
            <div style={{ fontSize:11, color:C.text3 }}>
              {rec.empCode} · {rec.designation}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Division / HQ", width:140,
      render: (_, rec) => (
        <div>
          <Tag style={{ background:"#fef2f2", border:"1px solid #fca5a5", color:"#7f1d1d", borderRadius:6, fontSize:11 }}>
            {rec.division||"—"}
          </Tag>
          <div style={{ fontSize:11, color:C.text3 }}>{rec.hq}</div>
        </div>
      ),
    },
    {
      title: "Date of Joining", width:130,
      render: (_, rec) => (
        <div style={{ fontSize:12 }}>
          <div style={{ fontWeight:600, color:C.text2 }}>
            {rec.dateOfJoining ? dayjs(rec.dateOfJoining).format("DD MMM YYYY") : "—"}
          </div>
          <div style={{ color:C.text3, fontSize:11 }}>
            {rec.dateOfJoining ? `Day ${dayjs().diff(dayjs(rec.dateOfJoining),"day")}` : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Resign Date", width:120,
      sorter: (a,b) => (a.resignedDate||"").localeCompare(b.resignedDate||""),
      render: (_, rec) => rec.resignedDate
        ? <Tag color="red" style={{ borderRadius:20, fontWeight:600 }}>
            {dayjs(rec.resignedDate).format("DD MMM YYYY")}
          </Tag>
        : <span style={{ color:C.text3 }}>—</span>,
    },
    {
      title: "Tenure", width:100,
      render: (_, rec) => {
        if (!rec.dateOfJoining || !rec.resignedDate) return "—";
        const days = dayjs(rec.resignedDate).diff(dayjs(rec.dateOfJoining),"day");
        const months = Math.floor(days/30);
        return (
          <div style={{ fontSize:12 }}>
            <span style={{ fontWeight:700, color:"#7f1d1d" }}>{months}m</span>
            <span style={{ color:C.text3, fontSize:11 }}> ({days}d)</span>
          </div>
        );
      },
    },
    {
      title: "Resign Reason", width:180, ellipsis:true,
      render: (_, rec) => rec.resignedReason
        ? <Tooltip title={rec.resignedReason}>
            <span style={{ fontSize:12, color:C.text2 }}>{rec.resignedReason}</span>
          </Tooltip>
        : <span style={{ color:C.text3, fontSize:11 }}>—</span>,
    },
    {
      title: "Cohort", width:130,
      render: (_, rec) => (
        <div style={{ fontSize:11, color:C.text3 }}>
          {rec.cohortName || "—"}
        </div>
      ),
    },
    {
      title: "Journey", width:110, align:"center",
      render: () => (
        <Tag style={{
          borderRadius:20, fontWeight:700, fontSize:11,
          background:"#fef2f2", color:"#dc2626",
          border:"1px solid #fca5a5", padding:"3px 10px",
        }}>
          🚫 Closed
        </Tag>
      ),
    },
    {
      title: "Actions", width:80, align:"center", fixed:"right",
      render: (_, rec) => (
        <Tooltip title="View Profile">
          <Button size="small" icon={<EyeOutlined/>}
            onClick={() => navigate(`/employees/${rec.id}`)}
            style={{ borderRadius:7 }}/>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="emplist-root">

      {/* ── Redirect banner — shown when arriving from Cohort/Dashboard pending link ── */}
      {searchParams.get("status") === "pending" && (
        <div style={{
          background:"#fffbeb", border:"1.5px solid #f59e0b",
          borderRadius:10, padding:"10px 16px", marginBottom:12,
          display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
        }}>
          <span style={{ fontSize:13, color:"#92400e", fontWeight:600 }}>
            ⏳ Showing employees with overdue pending calls — redirected from Cohort Analytics
          </span>
          <button
            onClick={() => { setFilterStatus("all"); window.history.replaceState(null, "", "/employees"); }}
            style={{ background:"none", border:"none", cursor:"pointer",
              color:"#b45309", fontWeight:700, fontSize:12 }}
          >
            ✕ Clear
          </button>
        </div>
      )}

      {/* ── Header ── */}
      <div className="emplist-header">
        <div>
          <h2 className="emplist-title">Employees</h2>
          <p className="emplist-sub">
            {inJourneyKpiCount} active · {journeyCompletedEmployees.length} completed · {filteredResignedEmployees.length} resigned · {decPlusTotalEl} total
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>Refresh</Button>
          <Button icon={<PlusOutlined />} type="primary" onClick={() => navigate("/add")}>Add Employee</Button>
          <Button icon={<UploadOutlined />} onClick={() => navigate("/bulk-upload")} style={{ color: C.orange, borderColor: "#f0c48a" }}>Bulk Upload</Button>
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={handleExport}
            style={{
              background:   "#f0fdf4",
              borderColor:  "#86efac",
              color:        "#16a34a",
              fontWeight:   600,
            }}
          >
            {exporting ? "Generating…" : "Export to Excel"}
          </Button>
        </div>
      </div>

      {/* ══ Active / Resigned Tab Switcher ════════════════════════ */}
      <div style={{ display:"flex", gap:0, marginBottom:16,
        background:"#fff", borderRadius:12,
        border:"1px solid #dce6f0", overflow:"hidden",
        boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
        alignSelf:"flex-start",
      }}>
        {[
          { key:"active",        label:"Active Employees",   icon:"✅", count:inJourneyKpiCount,                color:"#16a34a" },
          { key:"completed",     label:"Journey Completed",  icon:"🎓", count:journeyCompletedEmployees.length, color:"#7c3aed" },
          { key:"pending_calls", label:"Pending Calls",      icon:"⏳", count:pendingCallsEmployees.length,     color:"#E8871A" },
          { key:"resigned",      label:"Resigned Employees", icon:"🚪", count:filteredResignedEmployees.length, color:"#dc2626" },
        ].map(tab => {
          const active = activeTab === tab.key;
          return (
            <button key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setFilterStatus("all");
                setFilterDesig("all");
                setFilterDiv("all");
                setSearch("");
                setPage(1);
              }}
              style={{
                padding:"12px 28px", cursor:"pointer",
                border:"none",
                borderBottom:active ? `3px solid ${tab.color}` : "3px solid transparent",
                background:active ? "#fff" : "#f5f8fc",
                fontWeight:active ? 800 : 600,
                fontSize:14,
                color:active ? tab.color : "#7a96b0",
                transition:"all .15s",
                display:"flex", alignItems:"center", gap:8,
              }}>
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              <span style={{
                fontSize:12, fontWeight:800,
                padding:"1px 8px", borderRadius:20,
                background: active ? tab.color+"18" : "#eaf1f8",
                color: active ? tab.color : "#7a96b0",
              }}>{tab.count}</span>
            </button>
          );
        })}
      </div>

      {/* ══ Status KPI chips ══════════════════════════════════════ */}
      <div className="emplist-chips" style={{ marginBottom:8 }}>
        {(activeTab === "resigned"
          ? [
              { label:"All Resigned", value:computedKpi.total, key:"all",
                color:"#dc2626", bg:"#fef2f2", icon:"🚪" },
            ]
          : activeTab === "completed"
          ? [
              { label:"Journey Completed", value:computedKpi.total, key:"all",
                color:"#7c3aed", bg:"#f5f3ff", icon:"🎓" },
            ]
          : activeTab === "pending_calls"
          ? [
              { label:"All Pending", value:computedKpi.total, key:"all",
                color:"#E8871A", bg:"#fff7ed", icon:"⏳" },
            ]
          : [
              { label:"All Employees",  value:computedKpi.total,      key:"all",       color:"#1B2D6B", bg:"#eff6ff", icon:"👥" },
              { label:"Due Today",      value:computedKpi.todayCount, key:"today",     color:"#dc2626", bg:"#fff5f5", icon:"🔴" },
              { label:"Next 7 Days",    value:computedKpi.week7Count, key:"next7days", color:"#d97706", bg:"#fffbeb", icon:"🟡" },
              { label:"Overdue / Pending", value:computedKpi.pending, key:"pending",  color:"#E8871A", bg:"#fff7ed", icon:"⏳" },
              { label:"Missed Calls",   value:computedKpi.missed,      key:"missed",      color:"#dc2626", bg:"#fef2f2", icon:"🚫" },
              { label:"No Response",    value:computedKpi.noResponse,  key:"noresponse",  color:"#be185d", bg:"#fdf2f8", icon:"🔕" },
              { label:"New / Not Started", value:computedKpi.new,      key:"upcoming",    color:"#6366f1", bg:"#f5f3ff", icon:"🆕" },
            ]
        ).map(chip => {
          const active = filterStatus === chip.key;
          return (
            <div
              key={chip.key}
              onClick={() => { setFilterStatus(chip.key); setPage(1); }}
              style={{
                display:"flex", flexDirection:"column", alignItems:"center",
                padding:"12px 20px", minWidth:100,
                background: active ? chip.color : chip.bg,
                border:`2px solid ${active ? chip.color : chip.color+'44'}`,
                borderRadius:14, cursor:"pointer",
                boxShadow: active ? `0 4px 16px ${chip.color}40` : "0 1px 3px rgba(0,0,0,0.05)",
                transition:"all .18s",
                transform: active ? "translateY(-2px)" : "",
              }}
              onMouseEnter={e=>{ if(!active){ e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow=`0 4px 12px ${chip.color}30`; }}}
              onMouseLeave={e=>{ if(!active){ e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow="0 1px 3px rgba(0,0,0,0.05)"; }}}
            >
              <span style={{ fontSize:20, marginBottom:4 }}>{chip.icon}</span>
              <span style={{
                fontSize:26, fontWeight:900, lineHeight:1, marginBottom:3,
                color: active ? "#fff" : chip.color,
                letterSpacing:"-1px",
              }}>{chip.value ?? "—"}</span>
              <span style={{
                fontSize:10, fontWeight:700, textTransform:"uppercase",
                letterSpacing:"0.6px", textAlign:"center",
                color: active ? "rgba(255,255,255,.85)" : "#7a96b0",
              }}>{chip.label}</span>
            </div>
          );
        })}
      </div>

      {/* ══ Designation KPI chips ══════════════════════════════════ */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:16 }}>
        <div
          onClick={() => { setFilterDesig("all"); setPage(1); }}
          style={{
            padding:"5px 14px", borderRadius:20, cursor:"pointer",
            fontSize:12, fontWeight:700,
            background: filterDesig==="all" ? "#1B2D6B" : "#eff6ff",
            color: filterDesig==="all" ? "#fff" : "#1B2D6B",
            border:`1.5px solid ${filterDesig==="all" ? "#1B2D6B" : "#1B2D6B44"}`,
            transition:"all .15s",
          }}>
          All Designations · {tabEmployees.length}
        </div>
        {designationCounts.map(({ designation, count }) => {
          const active = filterDesig === designation;
          return (
            <div
              key={designation}
              onClick={() => { setFilterDesig(designation); setPage(1); }}
              style={{
                padding:"5px 14px", borderRadius:20, cursor:"pointer",
                fontSize:12, fontWeight:700,
                background: active ? "#1E6FBF" : "#f5f8fc",
                color: active ? "#fff" : "#4a6580",
                border:`1.5px solid ${active ? "#1E6FBF" : "#dce6f0"}`,
                transition:"all .15s",
                display:"flex", alignItems:"center", gap:6,
              }}
              onMouseEnter={e=>{ if(!active){ e.currentTarget.style.background="#eaf1f8"; e.currentTarget.style.borderColor="#1E6FBF44"; }}}
              onMouseLeave={e=>{ if(!active){ e.currentTarget.style.background="#f5f8fc"; e.currentTarget.style.borderColor="#dce6f0"; }}}
            >
              <span>{designation}</span>
              <span style={{
                background: active ? "rgba(255,255,255,.25)" : "#dce6f0",
                color: active ? "#fff" : "#4a6580",
                borderRadius:20, padding:"1px 7px", fontSize:11, fontWeight:800,
              }}>{count}</span>
            </div>
          );
        })}
      </div>

      {/* ── Filters ── */}
      <div className="emplist-filters">
        <Input
          prefix={<SearchOutlined style={{ color: C.text3 }} />}
          placeholder="Search name, code, HQ, manager…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          allowClear
          style={{ width: 280, borderRadius: 9 }}
        />
        <Select
          value={filterDiv}
          onChange={v => { setFilterDiv(v); setPage(1); }}
          style={{ width: 160 }}
          placeholder="All Divisions"
        >
          <Option value="all">All Divisions</Option>
          {divisions.map(d => <Option key={d} value={d}>{d}</Option>)}
        </Select>
        <Select
          value={filterDesig}
          onChange={setFilterDesig}
          style={{ width: 160 }}
          placeholder="All Designations"
        >
          <Option value="all">All Designations</Option>
          {designations.map(d => <Option key={d} value={d}>{d}</Option>)}
        </Select>
        <span className="filter-count">
          Showing <strong style={{ color: C.text1 }}>{filtered.length}</strong> of {employees.length}
        </span>
        {(search || filterDiv !== "all" || filterDesig !== "all" || filterStatus !== "all") && (
          <Button
            size="small"
            onClick={() => { setSearch(""); setFilterDiv("all"); setFilterDesig("all"); setFilterStatus("all"); setPage(1); }}
            style={{ borderRadius: 7 }}
          >
            Clear Filters
          </Button>
        )}
      </div>

      {/* ── Table ── */}
      <div className="emplist-table-wrap">
        <Table
          dataSource={filtered}
          columns={activeTab === "resigned" ? resignedColumns : columns}
          rowKey="id"
          loading={loading}
          rowClassName={activeTab === "resigned"
            ? (rec => rec.isResigned ? "row-resigned" : "")
            : activeTab === "completed"
            ? (() => "row-completed")
            : rowClassName}
          rowSelection={{ selectedRowKeys: selectedRows, onChange: setSelectedRows }}
          pagination={{
            current:         page,
            pageSize:        limit,
            total:           filtered.length,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (t, r) => (
              <span style={{ color: C.text3, fontSize: 12 }}>{r[0]}–{r[1]} of {t} employees</span>
            ),
            onChange: (p, l) => { setPage(p); setLimit(l); },
          }}
          scroll={{ x: 1300 }}
          size="small"
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div style={{ padding: "20px 0" }}>
                    <div style={{ color: C.text3, marginBottom: 14, fontSize: 13 }}>
                      {search || filterDiv !== "all" || filterStatus !== "all"
                        ? "No employees match your filters."
                        : "No employees yet."}
                    </div>
                    {!search && filterDiv === "all" && filterStatus === "all" && (
                      <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/add")}>
                        Add Employee
                      </Button>
                    )}
                  </div>
                }
              />
            ),
          }}
        />
      </div>

      {/* ── Bulk action bar ── */}
      {selectedRows.length > 0 && (
        <div className="bulk-action-bar">
          <span style={{ color: C.text2, fontSize: 13 }}>
            <strong style={{ color: C.text1 }}>{selectedRows.length}</strong> employees selected
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Popconfirm
              title={`Delete ${selectedRows.length} employees?`}
              description="This cannot be undone."
              onConfirm={handleDeleteSelected}
              okText="Delete All"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 7 }}>
                Delete Selected
              </Button>
            </Popconfirm>
            <Button size="small" onClick={() => setSelectedRows([])} style={{ borderRadius: 7 }}>
              Deselect
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}